import {
  canInheritPreviousReuse,
  validatePickSchedule,
  type PickRequirement,
} from "@sportsmet/contest-engine";
import {
  addPickRange,
  PICK_SCHEDULE_ERROR_SUMMARY,
  pickRangeFieldErrors,
  setPickRangeStart,
} from "@sportsmet/contest-engine/rules-draft";
import { Button, Checkbox, Input, Select } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { countOf } from "../survivorGame/survivorGameCopy";
import { RulesSection } from "./RulesSection";
import type { typeSurvivorRulesDraft } from "./survivorRulesDraft";

const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
].map((label, index) => ({ label, value: String(index + 1) }));

function deviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The timezone choices: every IANA zone the runtime knows, with the range's
 * current zone and the device's zone always present.
 */
function timeZoneItems(current: string): { value: string; label: string }[] {
  const known =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : [];
  const zones = [
    ...new Set([current, deviceTimeZone(), ...known].filter(Boolean)),
  ].sort();
  return zones.map((zone) => ({
    value: zone,
    label: zone.replaceAll("_", " "),
  }));
}

export function PickScheduleEditor({
  rules,
  onChangeRules,
}: {
  rules: typeSurvivorRulesDraft;
  onChangeRules: (rules: typeSurvivorRulesDraft) => void;
}): ReactNode {
  const requirements = rules.pickSchedule?.requirements ?? [];
  const errors = validatePickSchedule(
    rules.pickSchedule,
    rules.endDateMode === "date" ? rules.endDate : null,
    rules.startDate,
  );
  function update(index: number, patch: Partial<PickRequirement>): void {
    onChangeRules({
      ...rules,
      pickSchedule: {
        version: 1,
        requirements: requirements.map((item, i) =>
          i === index ? { ...item, ...patch } : item,
        ),
      },
    });
  }
  function changeStart(index: number, startsOn: string): void {
    onChangeRules({
      ...rules,
      pickSchedule: {
        version: 1,
        requirements: setPickRangeStart(requirements, index, startsOn),
      },
    });
  }
  function add(): void {
    onChangeRules({
      ...rules,
      pickSchedule: {
        version: 1,
        requirements: addPickRange({
          rules,
          requirementId: crypto.randomUUID(),
          defaultTimeZone: deviceTimeZone(),
        }),
      },
    });
  }
  return (
    <RulesSection
      title="Pick schedule"
      icon="calendar"
      description="Set different requirements for different dates. Games must be scheduled within the range they count toward; picking early does not change eligibility."
      summary={
        requirements.length
          ? `${requirements.length} requirement range${requirements.length === 1 ? "" : "s"}`
          : "One requirement for the whole contest"
      }
      advanced={requirements.length === 0}
    >
      <p className="text-caption text-ink-secondary">
        Without a schedule, the contest uses the picking rules above. Scheduled
        ranges replace those defaults for their rounds. Opened rounds retain
        their saved rules.
      </p>
      {requirements.map((item, index) => {
        const fieldErrors = pickRangeFieldErrors({ errors, index, item });
        return (
          <fieldset
            key={item.requirementId}
            className="flex flex-col gap-4 rounded-lg border border-hairline p-4"
          >
            <legend className="text-body-strong text-ink">
              Requirement {index + 1}
            </legend>
            <Input
              label="Picks per round"
              type="number"
              min={1}
              max={1000}
              value={item.requiredPicks}
              error={fieldErrors.requiredPicks}
              onChange={(e) => {
                update(index, { requiredPicks: Number(e.target.value) });
              }}
            />
            <Select
              label="How often"
              value={item.cadence}
              items={[
                { value: "week", label: "Each week" },
                { value: "day", label: "Each game day" },
              ]}
              onValueChange={(value) => {
                update(index, { cadence: value as PickRequirement["cadence"] });
              }}
            />
            <Input
              label="Requirement starts"
              type="date"
              value={item.startsOn}
              error={fieldErrors.startsOn}
              onChange={(e) => {
                changeStart(index, e.target.value);
              }}
            />
            <Input
              label="Requirement ends"
              type="date"
              value={item.endsOn ?? ""}
              hint="Leave blank only for the last range, to follow the contest end."
              error={fieldErrors.endsOn}
              onChange={(e) => {
                update(index, { endsOn: e.target.value || null });
              }}
            />
            <Select
              label="Schedule timezone"
              value={item.timeZone}
              items={timeZoneItems(item.timeZone)}
              hint="Dates and daylight-saving changes follow this timezone."
              error={fieldErrors.timeZone}
              onValueChange={(value) => {
                update(index, { timeZone: value });
              }}
            />
            {item.cadence === "week" ? (
              <Select
                label="Week starts on"
                value={String(item.weekStartDay ?? rules.weekStartDay)}
                items={WEEKDAYS}
                onValueChange={(value) => {
                  update(index, { weekStartDay: Number(value) });
                }}
              />
            ) : null}
            <Checkbox
              label="Allow teams to be picked again in this range"
              checked={item.teamReuseAllowed}
              onCheckedChange={(checked) => {
                update(index, {
                  teamReuseAllowed: checked === true,
                  ...(checked
                    ? {
                        inheritPreviousReuse: false,
                        extendPreviousGames: false,
                      }
                    : {}),
                });
              }}
            />
            <RulesSection
              title="Advanced rules"
              description="Deadlines, revived players and continuity with the previous range."
              advanced
              summary={`${item.lockTiming === "firstGame" ? "First" : "Last"} game deadline · ${countOf(item.revivedRequiredPicks ?? item.requiredPicks, "pick", "picks")} for revived players`}
            >
              <Select
                label="Round deadline"
                value={item.lockTiming}
                items={[
                  { value: "firstGame", label: "Start of the first game" },
                  { value: "lastGame", label: "Start of the last game" },
                ]}
                hint="Each game still stops accepting picks at its own kickoff."
                onValueChange={(value) => {
                  update(index, {
                    lockTiming: value as PickRequirement["lockTiming"],
                  });
                }}
              />
              <Input
                label="Picks required from revived players"
                type="number"
                min={1}
                max={1000}
                value={item.revivedRequiredPicks ?? ""}
                hint="Leave blank to use the normal count. A commissioner’s entry-specific override takes precedence."
                error={fieldErrors.revivedRequiredPicks}
                onChange={(e) => {
                  update(index, {
                    revivedRequiredPicks:
                      e.target.value === "" ? null : Number(e.target.value),
                  });
                }}
              />
              {canInheritPreviousReuse(requirements, item.requirementId) ? (
                <Checkbox
                  label="Keep previous range’s used-team restrictions"
                  description="Off starts fresh team history for this range. On keeps the previous range’s used teams, including teams used while reuse was allowed, unavailable here. History continues through linked ranges until a range starts fresh. Games and pick credit never carry across ranges."
                  checked={item.inheritPreviousReuse}
                  onCheckedChange={(checked) => {
                    update(index, { inheritPreviousReuse: checked === true });
                  }}
                />
              ) : null}
              {item.inheritPreviousReuse &&
              !canInheritPreviousReuse(requirements, item.requirementId) ? (
                <div role="alert">
                  This history link needs a bounded preceding range with team
                  reuse off in this range. Restore those settings or remove the
                  link before saving.
                  <Button
                    variant="secondary"
                    onClick={() => {
                      update(index, { inheritPreviousReuse: false });
                    }}
                  >
                    Remove incompatible history extension
                  </Button>
                </div>
              ) : null}
              <Select
                label="Permanently canceled game"
                value={item.postponementPolicy}
                items={[
                  {
                    value: "makeUpNextRound",
                    label: "Replace before lock; make up after lock",
                  },
                  { value: "countAsLoss", label: "Count as a loss" },
                ]}
                onValueChange={(value) => {
                  update(index, {
                    postponementPolicy:
                      value as PickRequirement["postponementPolicy"],
                  });
                }}
              />
            </RulesSection>
            <Button
              variant="secondary"
              onClick={() => {
                const next = requirements.filter((_, i) => i !== index);
                onChangeRules({
                  ...rules,
                  pickSchedule: next.length
                    ? { version: 1, requirements: next }
                    : null,
                });
              }}
            >
              Remove requirement {index + 1}
            </Button>
          </fieldset>
        );
      })}
      {errors.length ? (
        <p role="alert" className="text-caption text-error">
          {PICK_SCHEDULE_ERROR_SUMMARY}
        </p>
      ) : null}
      <Button
        variant="secondary"
        onClick={add}
        disabled={requirements.length >= 100}
      >
        Add requirement range
      </Button>
    </RulesSection>
  );
}
